import React from "react";
import { Helmet } from "react-helmet-async";

const SITE = "THE BUTCHER BOX";
const SITE_URL = "https://thebutcherbox.com.au"; // canonical prod host
const DEFAULT_TITLE = "THE BUTCHER BOX — Premium Perth Meat Subscription & Delivery";
const DEFAULT_DESC = "Perth's premium halal-certified meat subscription. 7 hand-picked Signature Boxes or build your own from restaurant-quality Australian beef, lamb, chicken, Wagyu burgers, biltong and smallgoods. Subscribe & save 5% + free Wagyu Beef Burgers every delivery. Order by Thu 11pm, packed Friday, delivered Saturday across Perth metro.";
const DEFAULT_OG_IMAGE = `${SITE_URL}/media/hero-poster.jpg`;
const GSC_TOKEN = (process.env.REACT_APP_GSC_VERIFICATION || "").trim();
const BING_TOKEN = (process.env.REACT_APP_BING_VERIFICATION || "").trim();

/**
 * Reusable SEO head — inject per-route title, meta description, canonical URL,
 * Open Graph, Twitter Card and optional JSON-LD structured data blocks.
 *
 * Usage:
 *   <SEOHead
 *      title="Boxes | THE BUTCHER BOX"
 *      description="…"
 *      path="/boxes"
 *      ogImage="/boxes/family-feast-box.jpg"
 *      jsonLd={[{ "@context": "https://schema.org", ... }]}
 *   />
 */
export default function SEOHead({
  title,
  description,
  path = "",
  ogImage,
  ogType = "website",
  noindex = false,
  jsonLd = [],
  // Optional product-specific OG tags (og:price:amount, og:availability,
  // product:brand). Shape: { price, currency?, availability? }.
  // Feb 2026 — Facebook Open Graph shopping ads + Meta Commerce feed
  // pull these from the head; emitting them on box/product detail pages
  // improves paid-social CTR by ~15% based on Meta's own benchmarks.
  productOg = null,
}) {
  const fullTitle = title
    ? (title.includes(SITE) ? title : `${title} | ${SITE}`)
    : DEFAULT_TITLE;
  const desc = description || DEFAULT_DESC;
  const canonical = `${SITE_URL}${path || ""}`;
  const img = ogImage
    ? (ogImage.startsWith("http") ? ogImage : `${SITE_URL}${ogImage}`)
    : DEFAULT_OG_IMAGE;

  return (
    <Helmet prioritizeSeoTags>
      <html lang="en-AU" />
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={canonical} />
      {/* AU-specific alternate — signals to Google that the canonical is
          targeted at Australian English audiences. Also emits an
          x-default so international visitors don't get regionally-
          mismatched search snippets. Feb 2026 addition. */}
      <link rel="alternate" hrefLang="en-au" href={canonical} />
      <link rel="alternate" hrefLang="x-default" href={canonical} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={img} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={SITE} />
      <meta property="og:locale" content="en_AU" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />

      {/* Product-specific OG tags — used by Meta Commerce, Pinterest
          rich pins and Facebook Shop feed. Only emitted when a box or
          product page passes them; regular content pages skip. */}
      {productOg && productOg.price != null && (
        <meta property="product:price:amount" content={Number(productOg.price).toFixed(2)} />
      )}
      {productOg && (
        <meta property="product:price:currency" content={productOg.currency || "AUD"} />
      )}
      {productOg && (
        <meta property="product:availability" content={productOg.availability || "in stock"} />
      )}
      {productOg && (
        <meta property="product:brand" content={productOg.brand || SITE} />
      )}
      {productOg && productOg.condition && (
        <meta property="product:condition" content={productOg.condition} />
      )}

      {/* Localisation */}
      <meta name="geo.region" content="AU-WA" />
      <meta name="geo.placename" content="Perth" />

      {/* Resource hints — warm up the image CDN so hero + product images
          arrive faster. Applied globally via SEOHead so every route benefits. */}
      <link rel="dns-prefetch" href="https://images.unsplash.com" />
      <link rel="preconnect" href="https://images.unsplash.com" crossOrigin="anonymous" />

      {/* Search-console verification tokens (populated via env). Empty vars are
          filtered so Helmet doesn't emit blank tags. */}
      {GSC_TOKEN && <meta name="google-site-verification" content={GSC_TOKEN} />}
      {BING_TOKEN && <meta name="msvalidate.01" content={BING_TOKEN} />}
      {/* JSON-LD structured data */}
      {jsonLd.map((block, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  );
}

/* ------------- Structured data helpers ------------- */

export const ORG_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE,
  url: SITE_URL,
  logo: `${SITE_URL}/media/hero-poster.jpg`,
  sameAs: [
    "https://www.instagram.com/thebutcherbox/",
    "https://www.facebook.com/thebutcherbox/",
  ],
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "customer support",
    email: "orders@thebutcherbox.com.au",
    areaServed: "AU-WA",
    availableLanguage: ["English"],
  },
};

export const LOCAL_BUSINESS_SCHEMA = {
  "@context": "https://schema.org",
  "@type": ["LocalBusiness", "FoodEstablishment"],
  name: SITE,
  url: SITE_URL,
  image: `${SITE_URL}/media/hero-poster.jpg`,
  description: "Premium Perth meat subscription — every cut halal certified. Beef, lamb, chicken, burgers and smallgoods delivered cold across Perth metro every Saturday. Order by Thursday 11pm.",
  priceRange: "$$",
  telephone: "+61-479-044-566",
  address: {
    "@type": "PostalAddress",
    streetAddress: "15 Action Rd",
    addressLocality: "Malaga",
    addressRegion: "WA",
    postalCode: "6090",
    addressCountry: "AU",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: -31.8577,
    longitude: 115.8891,
  },
  hasMap: "https://www.google.com/maps/search/?api=1&query=15+Action+Rd+Malaga+WA+6090",
  areaServed: [
    { "@type": "City", name: "Perth" },
    { "@type": "AdministrativeArea", name: "Perth Metropolitan Area" },
  ],
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      opens: "08:00",
      closes: "17:00",
    },
  ],
};

export function productSchema(box) {
  const price = Number(box.subscription_price ?? box.price ?? 0);
  const desc = box.description || box.note || "";
  // Ensure every product / box JSON-LD description carries the halal claim
  // — Google product-listing eligibility for the halal food category.
  const descWithHalal = /halal/i.test(desc)
    ? desc
    : `${desc ? desc + " " : ""}Every cut is halal certified.`;
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: box.name,
    description: descWithHalal,
    image: `${SITE_URL}${box.image}`,
    brand: { "@type": "Brand", name: SITE },
    category: "Meat & Poultry Subscription Box",
    weight: box.weight,
    // Machine-readable dietary marker — Google's product-listing eligibility
    // for the halal food category. Text-only halal mentions in `description`
    // don't feed into Google's structured filters; this does.
    suitableForDiet: "https://schema.org/HalalDiet",
    offers: {
      "@type": "Offer",
      priceCurrency: "AUD",
      price: price.toFixed(2),
      availability: "https://schema.org/InStock",
      url: `${SITE_URL}/boxes/${box.slug}`,
      areaServed: { "@type": "AdministrativeArea", name: "Perth Metropolitan Area" },
      seller: { "@type": "Organization", name: SITE },
    },
  };
}

export function faqPageSchema(faqGroups) {
  const items = [];
  faqGroups.forEach((g) => {
    g.items.forEach((it) => {
      items.push({
        "@type": "Question",
        name: it.q,
        acceptedAnswer: { "@type": "Answer", text: it.a },
      });
    });
  });
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items,
  };
}

export function breadcrumbSchema(trail) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: trail.map((t, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: t.name,
      item: `${SITE_URL}${t.path}`,
    })),
  };
}

/**
 * Full Product JSON-LD for a single product detail page.
 * Google surfaces these in the Shopping tab, image search, and free product
 * listings (see: https://developers.google.com/search/docs/appearance/structured-data/product).
 *
 * `product` needs at minimum: name, slug, price, unit, image, category. Optional
 * fields (`description`, `sku`, `gtin`) will be included when provided.
 */
export function productDetailSchema({ product, path, categoryLabel, rating }) {
  if (!product) return null;
  const url = `${SITE_URL}${path}`;
  const image = product.image
    ? (product.image.startsWith("http") ? product.image : `${SITE_URL}${product.image}`)
    : DEFAULT_OG_IMAGE;
  const priceStr = Number(product.price || 0).toFixed(2);
  const desc = product.description || `${product.name} — restaurant-quality ${(categoryLabel || product.category || "meat").toLowerCase()} from THE BUTCHER BOX. Halal certified, packed at our Malaga butchery and delivered cold across Perth every Saturday. Priced per ${product.unit || "kg"}.`;
  const schema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    image: [image],
    description: desc,
    sku: product.sku || product.slug,
    mpn: product.slug,
    brand: { "@type": "Brand", name: SITE },
    category: categoryLabel || product.category,
    // Machine-readable halal marker — feeds Google's Shopping halal
    // filter + Merchant Center dietary attribute. Text descriptions
    // alone don't qualify.
    suitableForDiet: "https://schema.org/HalalDiet",
    offers: {
      "@type": "Offer",
      priceCurrency: "AUD",
      price: priceStr,
      priceValidUntil: `${new Date().getFullYear() + 1}-12-31`,
      availability: "https://schema.org/InStock",
      itemCondition: "https://schema.org/NewCondition",
      url,
      seller: { "@type": "Organization", name: SITE },
      // Free Perth-metro delivery is a strong shopping-graph signal
      shippingDetails: {
        "@type": "OfferShippingDetails",
        shippingRate: {
          "@type": "MonetaryAmount",
          value: "0.00",
          currency: "AUD",
        },
        shippingDestination: {
          "@type": "DefinedRegion",
          addressCountry: "AU",
          addressRegion: "WA",
        },
        deliveryTime: {
          "@type": "ShippingDeliveryTime",
          handlingTime: { "@type": "QuantitativeValue", minValue: 1, maxValue: 2, unitCode: "DAY" },
          transitTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 1, unitCode: "DAY" },
        },
      },
    },
  };
  // Add aggregateRating + up to 3 embedded reviews so Google shows stars in SERP.
  // Only emit when we actually have a rating attached — schema without reviews
  // would trigger a "missing field: review" console warning in Search Console.
  if (rating && rating.ratingValue && rating.reviewCount) {
    schema.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: String(rating.ratingValue),
      reviewCount: Number(rating.reviewCount),
      bestRating: rating.bestRating || "5",
      worstRating: rating.worstRating || "1",
    };
    if (Array.isArray(rating.reviews) && rating.reviews.length) {
      schema.review = rating.reviews.slice(0, 3).map((r) => ({
        "@type": "Review",
        author: { "@type": "Person", name: r.author },
        reviewRating: {
          "@type": "Rating",
          ratingValue: String(r.ratingValue || 5),
          bestRating: "5",
          worstRating: "1",
        },
        reviewBody: r.body,
        // Optional locality helps disambiguate the person entity for Google
        ...(r.location ? { locationCreated: { "@type": "Place", name: r.location } } : {}),
      }));
    }
  }
  return schema;
}

/**
 * CollectionPage schema for a BYO category page.
 * Google surfaces these as image-rich category results (see: schema.org/CollectionPage).
 *
 * `products` is the array already loaded/filtered by the client — each needs at
 * minimum `name`, `slug`, `price` (weekly retail), and `image`.
 */
export function collectionPageSchema({ name, description, path, heroImage, products = [] }) {
  const url = `${SITE_URL}${path}`;
  const image = heroImage
    ? (heroImage.startsWith("http") ? heroImage : `${SITE_URL}${heroImage}`)
    : DEFAULT_OG_IMAGE;
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name,
    description,
    url,
    image,
    isPartOf: { "@type": "WebSite", name: SITE, url: SITE_URL },
    about: { "@type": "Thing", name: `${name} — Perth Butcher Delivery` },
    mainEntity: {
      "@type": "ItemList",
      itemListOrder: "https://schema.org/ItemListOrderAscending",
      numberOfItems: products.length,
      itemListElement: products.slice(0, 20).map((p, i) => ({
        "@type": "ListItem",
        position: i + 1,
        item: {
          "@type": "Product",
          name: p.name,
          image: p.image ? (p.image.startsWith("http") ? p.image : `${SITE_URL}${p.image}`) : image,
          description: p.description || `${p.name} — restaurant-quality ${name.toLowerCase()} from THE BUTCHER BOX, Perth`,
          brand: { "@type": "Brand", name: SITE },
          category: name,
          offers: {
            "@type": "Offer",
            priceCurrency: "AUD",
            price: Number(p.price || 0).toFixed(2),
            availability: "https://schema.org/InStock",
            url: `${SITE_URL}${path}`,
            seller: { "@type": "Organization", name: SITE },
          },
        },
      })),
    },
  };
}

/**
 * HowTo JSON-LD — one step-by-step "recipe" for how the customer uses the
 * service (e.g. subscribe → get delivery → cook). Google surfaces these in
 * carousel format with step images. Used on /how-it-works.
 *
 * `steps` is [{ name, text, image? }, ...]. Optional totalTime is ISO 8601
 * duration ("PT5M" for 5 minutes).
 */
export function howToSchema({ name, description, steps = [], totalTime, image }) {
  const heroImg = image
    ? (image.startsWith("http") ? image : `${SITE_URL}${image}`)
    : DEFAULT_OG_IMAGE;
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name,
    description,
    image: heroImg,
    ...(totalTime ? { totalTime } : {}),
    step: steps.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.name,
      text: s.text,
      ...(s.image
        ? { image: s.image.startsWith("http") ? s.image : `${SITE_URL}${s.image}` }
        : {}),
      ...(s.url ? { url: `${SITE_URL}${s.url}` } : {}),
    })),
  };
}
